import React, { useRef, useState } from "react";
import { RCCBadge, RCCButton, RCCDropDownItem, RCCGrid, RCCHookToast, RCCIcon, RCCInput, RCCServicesApi, RCCTooltip } from "react-common-components";
import { IForwardPagination } from "react-common-components/dist/components/Pagination/interface";
import { useForm } from "react-hook-form";
import AppIcons from "src/components/AppIcons";
import OptionsColumn from "src/components/DefaultColumnsList/Options";
import FiltersContainer from "src/components/FiltersContainer";
import PageTitle from "src/components/PageTitle";
import Pagination from "src/components/Pagination";
import Select from "src/components/Select";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import ModalIndicatorsPermissions from "src/components/ModalIndicatorsPermissions";
import { IModalIndicatorsPermissionsRef } from "src/components/ModalIndicatorsPermissions/interface";
import { IRefUserGroupModals } from "./interface";
import Modals from "./modals";

const UserGroup: React.FC = () => {
	const { translate, currentLanguage } = useTranslation();

	const paginationRef = useRef<IForwardPagination>(null);
	const refModals = useRef<IRefUserGroupModals>(null);
	const refModalIndicatorsPermissions = useRef<IModalIndicatorsPermissionsRef>(null);


	const [showFilters, setShowFilters] = useState(false);
	const [filters, setFilters] = useState<{ filter: string, admin: string }>({ filter: "", admin: "T" });

	const { addToast } = RCCHookToast();

	const { 
		handleSubmit : handleSubmitFilters, 
		register : registerFilters, 
		watch : watchFilters, 
		reset: resetFilters,
		control: controlFilters
	} = useForm();

	const submitFilters = (data: any) => {
		setFilters({ filter: data.filter, admin: data.admin.value || data.admin});
		paginationRef.current?.reload();
	};
  
	const colOptions = (row: any) => (
		<>
			{row.efa004Efa001Id && (
				<OptionsColumn position="leftEnd">
					<>
						<RCCDropDownItem
							selfEnclose
							onClick={() => { refModals.current?.actionButtonChange(row);  }}
						>
							<RCCIcon name="pencil" />
							{translate("Alterar")}
						</RCCDropDownItem>
						<RCCDropDownItem
							selfEnclose
							onClick={() => { copyUserGroup(row.efa004Id);  }}
						>
							<RCCIcon name="copy" />
							{translate("Copiar")}
						</RCCDropDownItem>
						<RCCDropDownItem
							selfEnclose
							onClick={() => { 
								refModalIndicatorsPermissions.current?.openModal({
									idEfa004: row.efa004Id
								}, `${translate("Grupo de Usuários")}: ${row.efa004Descricao}`);
						  }}
						>
							<RCCIcon name="lock" />
							{translate("Permissões de indicadores")}
						</RCCDropDownItem>
						<RCCDropDownItem 
							selfEnclose
							onClick={() => { refModals.current?.actionButtonDelete(row); }}
						>
							<RCCIcon name="trash" 
							/>
							{translate("Excluir")}
						</RCCDropDownItem>
					</>
				</OptionsColumn>
			)}
		</>
	);

	const colAdmin = (row: any) => (
		<RCCBadge color={row.efa004Administrador === "S" ? "green" : "red"}
			styleInline="font-size: 0.83rem;"
		>
			{row.efa004Administrador === "S" ? translate("Sim") : translate("Não")}
		</RCCBadge>
	);

	const copyUserGroup = async (id: number) => {
		try{
			await RCCServicesApi.putRequest({
				method: "UsersGroups/Copy",
				urlParameters : {
					id,
					currentLanguage  
				}
			});
			
			addToast({
				color: "green",
				description: translate("Grupo de usuários copiado com sucesso"),
				title: translate("Sucesso")
			});

			paginationRef.current?.reload();

		}catch(err: any){
			standardErrorMessages(err, translate("Erro ao buscar copiar grupo de usuários"), addToast);
		}
	};

	return (
		<>
			<PageTitle
				title={translate("Grupo de Usuários")}
				icon={() => <AppIcons name="configs" />}
			>
				<RCCTooltip
					text={translate("Novo Grupo de Usuários")}
					color="purple"
				>
					<RCCButton
						icon={{name: "plus", color: "#FFF", size: 18}}
						color="purple"
						layout="isRounded"
						onClick={() => { refModals.current?.actionButtonAdd(); }}
						size="md"
					/>
				</RCCTooltip>
				<RCCTooltip
					text={translate("Filtros")}
					color={showFilters || filters.filter || filters.admin !== "T" ? "blue" : "grey"}
				>
					<RCCButton
						icon={{name: "filter", color: "#FFF", size: 16}}
						color={showFilters || filters.filter || filters.admin !== "T" ? "blue" : "grey"}
						layout="isRounded"
						size="md"
						onClick={() => setShowFilters((prevState) => !prevState)}
					/>
				</RCCTooltip>
			</PageTitle>
			{/* Filters */}

			{showFilters && (
				<FiltersContainer
					onSubmitFilters={handleSubmitFilters(submitFilters)}
					resetFilters={() => resetFilters({ admin: {value: "T", label: translate("Todos")}, filter: ""})}
				>
					<RCCGrid
						container
						item
						spacing={2}
					>
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}
						>
							<Select 
								control={controlFilters}
								name="admin"
								label={translate("Administrador")}
								defaultValue={filters.admin === "S" ? 
									{value: "S", label: translate("Sim")} : 
									{value: "T", label: translate("Todos")}
								}
								submit="value and label"
								options={[{
									value: "S",
									label: translate("Sim")
								},{
									value: "N",
									label: translate("Não")
								},{
									value: "T",
									label: translate("Todos")
								}]}
							/>
						</RCCGrid>
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}
						>
							<RCCInput
								name="filter" 
								label={translate("Pesquisar")}
								register={registerFilters}
								defaultValue={watchFilters("filter")}
							/>
						</RCCGrid>
					</RCCGrid>
				
				</FiltersContainer>
			)}	

			<Pagination 
				ref={paginationRef}
				dataOrRoute="UsersGroups/List"
				filtersData={filters}   
				styleInline={{
					ulContainer: "max-height: unset; overflow: visible;"
				}}
				columns={[
					{
						idColumn: "efa004Descricao",
						nameColumn: translate("Descrição"),
						justify: "left",
						xs: 12,
						sm: 6,
						md: 6,
						truncate: true
					},{
						idColumn: "efa004Administrador",
						nameColumn: translate("Administrador"),
						justify: "left",
						xs: 12,
						sm: 5,
						md: 5,
						truncate: true,
						cellRenderer: colAdmin
					},{
						idColumn: "options",
						nameColumn: "",
						justify: "right",
						xs: 12,
						sm: 1,
						md: 1,
						cellRenderer: colOptions,
						disableSort: true
					}
				]}
			/>

			<Modals
				ref={refModals}
				paginationRef={paginationRef}
			/>

			<ModalIndicatorsPermissions 
				ref={refModalIndicatorsPermissions}
			/>

		</>
	);
};

export default UserGroup;
